import { v4 as uuid } from 'uuid';

import { AnyRestable } from '../../../../types';
import getCloudAPIHost from '../../get-cloud-api-host';
import { getUserUid } from '../../get-user-uid';
import { SafeLogErrorOptions } from '../../log-error';
import { SessionState } from '../../session-state';
import { getUrl, injectParamsToPath } from '../../url';
import { api, BodyParseMethod, RetryOptions } from '../api';

export const IDEMPOTENCY_KEY_HEADER = 'idempotency-key';

interface BaseRequestParams {
    method: 'PUT' | 'GET' | 'POST';
    path: string;
    params?: Record<string, string | number | boolean>;
    query?: Record<string, string | undefined>;
    keepalive?: boolean;
    retryOptions?: RetryOptions;
    timeout?: number;
    payload?: unknown;
    modifyResponse?(response: Response): AnyRestable;
    additionalHeaders?: Record<string, string>;
    /** Добавляет заголовок idempotency-key
     * @default true
     */
    withIdempotencyHeader?: boolean;
    bodyParseMethod?: BodyParseMethod;
}

type RequestParams = Omit<BaseRequestParams, 'method'>;

export class CloudApiTransport {
    private headers: Record<string, string> = {};
    private omitCredentials = false;

    public get<T>(params: Omit<RequestParams, 'payload'>) {
        return this.request<T>({
            ...params,
            method: 'GET',
        });
    }

    public put<T>(params: RequestParams) {
        return this.request<T>({
            ...params,
            method: 'PUT',
        });
    }

    public post<T>(params: RequestParams) {
        return this.request<T>({
            ...params,
            method: 'POST',
        });
    }

    private request<T>(params: BaseRequestParams) {
        const {
            method,
            path,
            params: pathParams,
            keepalive,
            payload,
            retryOptions = {},
            timeout,
            modifyResponse,
            additionalHeaders,
            withIdempotencyHeader = true,
            bodyParseMethod,
        } = params;
        const search = (() => {
            if (!params.query) {
                return;
            }

            const searchParams = new URLSearchParams();

            Object.entries(params.query).forEach(([key, value]) => {
                if (typeof value !== 'undefined' && value !== 'undefined') {
                    searchParams.append(key, value);
                }
            });

            return searchParams.toString();
        })();

        const resultPath = pathParams ? injectParamsToPath(path, pathParams) : path;
        const cloudApiHost = getCloudAPIHost();
        const url = getUrl(cloudApiHost, resultPath, search);

        const additional: SafeLogErrorOptions['additional'] = {
            kind: 'unable_connect_cloud_api',
            staticUrl: getUrl(cloudApiHost, path),
        };

        const headers = (() => {
            const accumulated: Record<string, string> = {
                'content-type': 'application/json',
                'Client-Instance-Id': SessionState.instanceId,
                ...additionalHeaders,
                ...this.headers,
            };
            const userUid = getUserUid();
            if (userUid) {
                accumulated['X-Uid'] = userUid;
            }
            if (withIdempotencyHeader) {
                accumulated[IDEMPOTENCY_KEY_HEADER] = uuid();
            }
            return accumulated;
        })();

        return api<T>(
            url,
            {
                method,
                keepalive,
                body: JSON.stringify(payload),
                headers,
                ...(this.omitCredentials ? { credentials: 'omit' } : {}),
            },
            { ...retryOptions, timeout, modifyResponse, bodyParseMethod },
            { method: 'cloudAPI', additional }
        );
    }

    public setHeader(key: string, value: string) {
        this.headers[key] = value;
    }

    public setApiAuthorizationHeader(token: string) {
        this.setHeader('Authorization', token);
    }

    public disableCredentials() {
        this.omitCredentials = true;
    }
}
